import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  staffWelcomeMessage,
  type AdminTeamAddInput,
  type AdminTeamInvitation,
  type AdminTeamLegacySubUser,
  type AdminTeamMember,
  type AdminTeamTournament,
} from "../lib/api";
import { TEAM_PROFILE_OPTIONS } from "../lib/teamProfiles";
import {
  isTeamScopeDirty,
  joinTournamentNames,
  scopeForExistingSubUser,
  teamScopeMissing,
  teamScopeReach,
  teamScopeSelectionError,
  teamScopeSummary,
} from "../lib/teamScope";
import FieldHint from "../components/FieldHint";
import GuideHelpButton from "../components/GuideHelpButton";
import TeamTournamentScopePicker, { type TeamScopeValue } from "../components/TeamTournamentScopePicker";

// Equipo del organizador a nivel admin (Eduardo 2026-09-09/14): se da de
// alta UNA vez. Desde 2026-09-29 se elige en el alta a qué torneos entra
// cada quien: todos (también los que el organizador cree después) o solo
// los que se marquen. Dentro de cada torneo ("Equipo del torneo") se puede
// cambiar el perfil o quitar a alguien solo de ese torneo.

interface NewSubUserForm {
  full_name: string;
  email: string;
  password: string;
  phone: string;
}

const EMPTY_NEW_USER: NewSubUserForm = { full_name: "", email: "", password: "", phone: "" };
const DEFAULT_SCOPE: TeamScopeValue = { scope: "all", selectedIds: [] };
const SCOPE_EDITOR_ID = "team-scope-editor";

// Tarjetas que explican cada perfil (lo que permite team_permissions.py).
const TEAM_PROFILE_CARDS: Array<{ value: string; title: string; text: string }> = [
  { value: "coadmin", title: "Coadmin", text: "Todo lo que tú puedes en sus torneos, menos borrarlos." },
  { value: "capturista", title: "Capturista", text: "Programa partidos, ajusta horarios y canchas, y captura resultados. No toca jugadores, cuadros ni cobros." },
  { value: "finanzas", title: "Finanzas", text: "Solo inscripciones y pagos: registra pagos, valida comprobantes y cobra." },
  { value: "arbitro", title: "Árbitro", text: "Solo ve y captura los partidos que le asignes. No ve inscripciones, pagos, draw ni otras categorías." },
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : (parts[0] ?? "?").slice(0, 2);
  return letters.toUpperCase();
}

/** «20 oct» para la fila de una invitación pendiente. */
function shortDate(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-MX", { day: "numeric", month: "short" }).replace(".", "");
}

function torneosLabel(count: number): string {
  return count === 1 ? "1 torneo" : `${count} torneos`;
}

/** Solo ids de torneos que el organizador tiene hoy (el backend rechaza los demás). */
function knownIds(ids: number[], tournaments: AdminTeamTournament[]): number[] {
  const owned = new Set(tournaments.map((t) => t.id));
  return ids.filter((id) => owned.has(id));
}

export default function AdminTeamPage() {
  const { token } = useAuth();
  const { askConfirm } = useAbortableConfirm([]);
  const [members, setMembers] = useState<AdminTeamMember[]>([]);
  const [othersSubUsers, setOthersSubUsers] = useState<AdminTeamLegacySubUser[]>([]);
  const [used, setUsed] = useState(0);
  const [maxSubUsers, setMaxSubUsers] = useState(10);
  const [tournamentsCount, setTournamentsCount] = useState(0);
  const [tournaments, setTournaments] = useState<AdminTeamTournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [mode, setMode] = useState<"new" | "existing" | "invite">("new");
  // Invitar a alguien que ya usa Torneos123 (Eduardo 2026-10-20): no cuenta
  // en el tope de sub-usuarios; entra solo cuando acepta.
  const [invitations, setInvitations] = useState<AdminTeamInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);
  const [editingInviteId, setEditingInviteId] = useState<string | null>(null);
  const [inviteEditScope, setInviteEditScope] = useState<TeamScopeValue>(DEFAULT_SCOPE);
  const [profile, setProfile] = useState<string>("capturista");
  const [newUser, setNewUser] = useState<NewSubUserForm>(EMPTY_NEW_USER);
  const [existingUserId, setExistingUserId] = useState<string>("");
  const [addScope, setAddScope] = useState<TeamScopeValue>(DEFAULT_SCOPE);

  // Editor de torneos de un miembro: se abre con «Cambiar torneos» y guarda
  // una sola vez con «Guardar» (antes cada casilla guardaba al momento).
  // Vive DEBAJO de la tabla, no como fila: en celular la tabla se desliza de
  // lado y una fila la dejaba fuera de la pantalla (revisión 2026-09-29).
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editScope, setEditScope] = useState<TeamScopeValue>(DEFAULT_SCOPE);
  // Miembro con "todos" al que se le quitó un torneo desde el Equipo de ese
  // torneo: guardar "todos" lo regresaría ahí. Solo si se marca a propósito.
  const [restoreMissing, setRestoreMissing] = useState(false);
  const editorTitleRef = useRef<HTMLHeadingElement | null>(null);
  const scopeButtonRefs = useRef(new Map<number, HTMLButtonElement>());
  const lastEditedRef = useRef<number | null>(null);
  const addSectionRef = useRef<HTMLElement | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  // Menú «⋯» de cada miembro (Quitar del Equipo).
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [openInviteMenu, setOpenInviteMenu] = useState<string | null>(null);

  useEffect(() => {
    if (openInviteMenu === null) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpenInviteMenu(null);
    }
    function onPointer(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!target?.closest?.("[data-team-menu]")) setOpenInviteMenu(null);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [openInviteMenu]);

  useEffect(() => {
    if (openMenuId === null) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpenMenuId(null);
    }
    function onPointer(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!target?.closest?.("[data-team-menu]")) setOpenMenuId(null);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [openMenuId]);

  // «+ Agregar a mi equipo» del encabezado: baja al formulario de alta.
  function goToAddForm() {
    const section = addSectionRef.current;
    if (!section) return;
    section.scrollIntoView?.({ behavior: "smooth", block: "start" });
    const target = firstFieldRef.current ?? section.querySelector<HTMLElement>("input, select, button");
    target?.focus({ preventScroll: true });
  }

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.tournaments.listMyTeam(token);
      setMembers(response.data.members);
      setOthersSubUsers(response.data.sub_users_without_team);
      setUsed(response.data.used);
      setMaxSubUsers(response.data.max_sub_users);
      setTournamentsCount(response.data.tournaments_count);
      setTournaments(response.data.tournaments);
      setInvitations(response.data.invitations ?? []);
      setForbidden(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
      } else {
        setError(err instanceof ApiError ? err.message : "Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const cupoLleno = used >= maxSubUsers;
  const selectedProfile = TEAM_PROFILE_OPTIONS.find((option) => option.value === profile);

  useEffect(() => {
    if (cupoLleno && mode === "new" && othersSubUsers.length > 0) setMode("existing");
  }, [cupoLleno, mode, othersSubUsers.length]);

  // Foco (teclado y lector de pantalla): al abrir el editor va a su título;
  // al cerrarlo regresa al «Cambiar torneos» de esa persona.
  useEffect(() => {
    if (editingUserId !== null) {
      lastEditedRef.current = editingUserId;
      editorTitleRef.current?.focus();
      return;
    }
    const previous = lastEditedRef.current;
    lastEditedRef.current = null;
    if (previous !== null) scopeButtonRefs.current.get(previous)?.focus();
  }, [editingUserId]);

  // Cada cambio de la pantalla borra los avisos viejos: si no, GlobalToasts
  // los vuelve a sacar como toast al redibujar (regla: el toast responde a
  // la acción de ese momento).
  function clearNotices() {
    setError(null);
    setMessage(null);
  }

  // Cambiar de modo empieza de cero: lo precargado de un sub-usuario
  // existente no pasa a una cuenta nueva (ni al revés).
  function handleModeChange(next: "new" | "existing" | "invite") {
    clearNotices();
    setMode(next);
    setExistingUserId("");
    setAddScope(DEFAULT_SCOPE);
  }

  function handleExistingChange(value: string) {
    clearNotices();
    setExistingUserId(value);
    const sub = othersSubUsers.find((item) => String(item.user_id) === value);
    setAddScope(sub ? scopeForExistingSubUser(sub.tournament_ids ?? [], tournaments) : DEFAULT_SCOPE);
  }

  async function handleAdd(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || saving) return;
    clearNotices();
    if (mode === "existing" && !existingUserId) {
      setError("Elige el sub-usuario que quieres agregar a tu Equipo.");
      return;
    }
    const selectedIds = knownIds(addScope.selectedIds, tournaments);
    const scopeError = teamScopeSelectionError(addScope.scope, selectedIds, "add");
    if (scopeError) {
      setError(scopeError);
      return;
    }
    if (mode === "invite") {
      const email = inviteEmail.trim();
      if (!email) {
        setError("Escribe el correo de su cuenta.");
        return;
      }
      setSaving(true);
      try {
        const response = await api.tournaments.inviteToMyTeam(token, {
          email,
          profile,
          scope: addScope.scope,
          tournament_ids: addScope.scope === "selected" ? selectedIds : [],
        });
        // Mismo mensaje exista o no la cuenta (no revelamos quién usa Torneos123).
        setMessage(response.data.message ?? "Listo. Si ese correo tiene cuenta en Torneos123, recibirá tu invitación.");
        setInviteEmail("");
        setAddScope(DEFAULT_SCOPE);
        await load();
      } catch (err) {
        setError(err instanceof ApiError ? err.message || "No se pudo enviar la invitación." : "Error de red. Intenta otra vez.");
      } finally {
        setSaving(false);
      }
      return;
    }
    setSaving(true);
    try {
      const scopePart = {
        scope: addScope.scope,
        tournament_ids: addScope.scope === "selected" ? selectedIds : [],
      };
      const payload: AdminTeamAddInput =
        mode === "new"
          ? {
              profile,
              new_user: {
                full_name: newUser.full_name.trim(),
                email: newUser.email.trim(),
                password: newUser.password,
                phone: newUser.phone.trim() || null,
              },
              ...scopePart,
            }
          : { profile, user_id: Number(existingUserId), ...scopePart };
      const response = await api.tournaments.addMyTeamMember(token, payload);
      const member = response.data.member;
      const reach = teamScopeReach(member.scope, member.tournaments_count);
      setMessage(
        mode === "new"
          ? `${member.full_name} ya es ${member.profile_label} ${reach}. ${staffWelcomeMessage(member.full_name, member.email, member.welcome_email)}`
          : `${member.full_name} agregado a tu Equipo como ${member.profile_label} ${reach}.`,
      );
      setNewUser(EMPTY_NEW_USER);
      setExistingUserId("");
      setAddScope(DEFAULT_SCOPE);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo agregar al Equipo." : "Error de red. Intenta otra vez.");
    } finally {
      setSaving(false);
    }
  }

  async function handleChangeProfile(member: AdminTeamMember, nextProfile: string) {
    if (!token || nextProfile === member.profile) return;
    setBusyUserId(member.user_id);
    clearNotices();
    try {
      await api.tournaments.updateMyTeamMember(token, member.user_id, { profile: nextProfile });
      // El cambio de perfil no le da torneos: los que se le quitaron desde
      // el Equipo de un torneo siguen fuera.
      setMessage(
        member.scope === "all" && teamScopeMissing(member, tournaments).length === 0
          ? `Perfil de ${member.full_name} actualizado en todos tus torneos.`
          : `Perfil de ${member.full_name} actualizado en sus torneos.`,
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo cambiar el perfil." : "Error de red. Intenta otra vez.");
    } finally {
      setBusyUserId(null);
    }
  }

  function openScopeEditor(member: AdminTeamMember) {
    clearNotices();
    if (editingUserId === member.user_id) {
      // ya está abierto: solo llevar el foco al editor
      editorTitleRef.current?.focus();
      return;
    }
    setEditingUserId(member.user_id);
    // Con "todos", la lista (si cambia a "solo estos") arranca con los
    // torneos donde está hoy.
    setEditScope({ scope: member.scope, selectedIds: member.tournament_ids });
    setRestoreMissing(false);
  }

  function closeScopeEditor() {
    clearNotices();
    setEditingUserId(null);
    setEditScope(DEFAULT_SCOPE);
    setRestoreMissing(false);
  }

  // A qué torneos entra (Eduardo 2026-09-15/29): todos (y los nuevos) o
  // solo los elegidos. Se guarda una vez, con «Guardar», y solo si algo
  // cambió: volver a guardar "todos" regresaría a la persona a los torneos
  // de donde se le quitó, y eso solo pasa si se marca a propósito.
  async function handleScopeSave(member: AdminTeamMember) {
    if (!token) return;
    if (!isTeamScopeDirty(member, editScope, tournaments, restoreMissing)) {
      closeScopeEditor();
      return;
    }
    clearNotices();
    const selectedIds = knownIds(editScope.selectedIds, tournaments);
    const scopeError = teamScopeSelectionError(editScope.scope, selectedIds, "edit");
    if (scopeError) {
      setError(scopeError);
      return;
    }
    setBusyUserId(member.user_id);
    try {
      const response = await api.tournaments.setMyTeamMemberTournaments(token, member.user_id, {
        scope: editScope.scope,
        tournament_ids: editScope.scope === "selected" ? selectedIds : [],
      });
      const updated = response.data.member;
      setMembers((current) => current.map((item) => (item.user_id === member.user_id ? updated : item)));
      setEditingUserId(null);
      setEditScope(DEFAULT_SCOPE);
      setRestoreMissing(false);
      setMessage(`${member.full_name} entra ${teamScopeReach(updated.scope, updated.tournament_ids.length)}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudieron cambiar sus torneos." : "Error de red. Intenta otra vez.");
    } finally {
      setBusyUserId(null);
    }
  }

  async function handleRemove(member: AdminTeamMember) {
    if (!token) return;
    if (!(await askConfirm(`¿Quitar a ${member.full_name} de tu Equipo? Perderá el acceso a tus torneos. Su cuenta no se borra: podrás volver a agregarla cuando quieras.`, { acceptLabel: "Sí, quitar del Equipo", tone: "danger" }))) {
      return;
    }
    setBusyUserId(member.user_id);
    clearNotices();
    try {
      await api.tournaments.removeMyTeamMember(token, member.user_id);
      if (editingUserId === member.user_id) {
        // su fila desaparece: no hay botón a dónde regresar el foco
        lastEditedRef.current = null;
        setEditingUserId(null);
        setRestoreMissing(false);
      }
      setMessage(`${member.full_name} ya no está en tu Equipo.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo quitar del Equipo." : "Error de red. Intenta otra vez.");
    } finally {
      setBusyUserId(null);
    }
  }

  async function runInviteAction(invitation: AdminTeamInvitation, action: (authToken: string) => Promise<string>) {
    if (!token) return;
    setBusyInviteId(invitation.id);
    clearNotices();
    try {
      setMessage(await action(token));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo completar." : "Error de red. Intenta otra vez.");
    } finally {
      setBusyInviteId(null);
    }
  }

  function handleInviteResend(invitation: AdminTeamInvitation) {
    void runInviteAction(invitation, async (authToken) => {
      await api.tournaments.resendMyTeamInvitation(authToken, invitation.id);
      return `Reenviamos la invitación a ${invitation.email}.`;
    });
  }

  async function handleInviteCancel(invitation: AdminTeamInvitation) {
    if (!(await askConfirm(`¿Cancelar la invitación a ${invitation.email}?`, { acceptLabel: "Sí, cancelar invitación", tone: "danger" }))) return;
    void runInviteAction(invitation, async (authToken) => {
      await api.tournaments.deleteMyTeamInvitation(authToken, invitation.id);
      return `Cancelaste la invitación a ${invitation.email}.`;
    });
  }

  async function handleInviteRemove(invitation: AdminTeamInvitation) {
    const name = invitation.full_name || invitation.email;
    if (!(await askConfirm(`¿Quitar a ${name} de tu equipo? Solo le quita el acceso a tus torneos. Su cuenta no se toca.`, { acceptLabel: "Sí, quitar del equipo", tone: "danger" }))) return;
    if (editingInviteId === invitation.id) setEditingInviteId(null);
    void runInviteAction(invitation, async (authToken) => {
      await api.tournaments.deleteMyTeamInvitation(authToken, invitation.id);
      return `${name} ya no está en tu equipo. Su cuenta sigue igual.`;
    });
  }

  function handleInviteProfile(invitation: AdminTeamInvitation, nextProfile: string) {
    if (nextProfile === invitation.profile) return;
    void runInviteAction(invitation, async (authToken) => {
      await api.tournaments.updateMyTeamInvitation(authToken, invitation.id, { profile: nextProfile });
      return `Perfil de ${invitation.full_name || invitation.email} actualizado en sus torneos.`;
    });
  }

  function openInviteScopeEditor(invitation: AdminTeamInvitation) {
    clearNotices();
    setEditingUserId(null);
    setEditingInviteId(invitation.id);
    setInviteEditScope({ scope: invitation.scope, selectedIds: invitation.tournament_ids });
  }

  async function handleInviteScopeSave(invitation: AdminTeamInvitation) {
    const selectedIds = knownIds(inviteEditScope.selectedIds, tournaments);
    const scopeError = teamScopeSelectionError(inviteEditScope.scope, selectedIds, "edit");
    if (scopeError) {
      clearNotices();
      setError(scopeError);
      return;
    }
    await runInviteAction(invitation, async (authToken) => {
      const response = await api.tournaments.setMyTeamInvitationTournaments(authToken, invitation.id, {
        scope: inviteEditScope.scope,
        tournament_ids: inviteEditScope.scope === "selected" ? selectedIds : [],
      });
      setEditingInviteId(null);
      const updated = response.data.invitation;
      return `${invitation.full_name || invitation.email} entra ${teamScopeReach(updated.scope, updated.tournaments_count)}.`;
    });
  }

  const editingInvite =
    editingInviteId === null ? null : invitations.find((item) => item.id === editingInviteId && item.status === "accepted") ?? null;

  function renderInviteScopeEditor(invitation: AdminTeamInvitation) {
    const busy = busyInviteId === invitation.id;
    const name = invitation.full_name || invitation.email;
    return (
      <div className="team-scope-editor" role="region" aria-label={`Torneos de ${name}`}>
        <h3 className="team-scope-editor__title">Torneos de {name}</h3>
        <TeamTournamentScopePicker
          idPrefix={`team-invite-${invitation.id.slice(0, 8)}`}
          value={inviteEditScope}
          tournaments={tournaments}
          onChange={setInviteEditScope}
          onInteract={clearNotices}
          disabled={busy}
          personLabel={name}
        />
        <div className="form__actions">
          <button type="button" className="btn btn--compact" onClick={() => void handleInviteScopeSave(invitation)} disabled={busy}>
            {busy ? "Guardando…" : "Guardar"}
          </button>
          <button type="button" className="btn btn--ghost btn--compact" onClick={() => setEditingInviteId(null)} disabled={busy}>
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  function renderInvitationRow(invitation: AdminTeamInvitation) {
    const busy = busyInviteId === invitation.id;
    if (invitation.status === "pending") {
      return (
        <tr key={invitation.id} className="adm-team__row adm-team__row--invite">
          <td className="adm-team__who team-members-table__name">
            <span className="adm-team__avatar adm-team__avatar--invite" aria-hidden="true">✉</span>
            <span className="adm-players__who-text">
              <strong className="adm-team__email-strong">{invitation.email}</strong>
              <span className="adm-players__sub">
                Invitada el {shortDate(invitation.created_at)} · {invitation.profile_label} ·{" "}
                {invitation.scope === "all" ? "Todos tus torneos" : torneosLabel(invitation.tournaments_count)}
              </span>
            </span>
          </td>
          <td className="adm-team__cell" colSpan={2}>
            <span className={`adm-team__badge ${invitation.expired ? "adm-team__badge--expired" : "adm-team__badge--pending"}`}>
              {invitation.expired ? "Invitación vencida" : "Invitación enviada · pendiente"}
            </span>
          </td>
          <td className="adm-team__cell adm-team__actions">
            <button
              type="button"
              className="btn btn--ghost btn--compact adm-team__edit"
              onClick={() => handleInviteResend(invitation)}
              disabled={busy}
              aria-label={`Reenviar invitación a ${invitation.email}`}
            >
              Reenviar
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--compact adm-team__edit adm-team__cancel"
              onClick={() => void handleInviteCancel(invitation)}
              disabled={busy}
              aria-label={`Cancelar invitación a ${invitation.email}`}
            >
              Cancelar
            </button>
          </td>
        </tr>
      );
    }
    const name = invitation.full_name || invitation.email;
    const menuKey = `inv-${invitation.id}`;
    const menuOpen = openInviteMenu === menuKey;
    return (
      <tr key={invitation.id} className="adm-team__row">
        <td className="adm-team__who team-members-table__name">
          <span className="adm-team__avatar" aria-hidden="true">{initialsOf(name)}</span>
          <span className="adm-players__who-text">
            <strong>{name}</strong>
            <span className="adm-players__sub adm-team__email">{invitation.email}</span>
            <span className="adm-team__badges">
              <span className="adm-team__badge adm-team__badge--own">Cuenta propia</span>
            </span>
          </span>
        </td>
        <td className="adm-team__cell adm-team__select-cell">
          <select
            className="t123-select"
            value={invitation.profile}
            onChange={(event) => handleInviteProfile(invitation, event.target.value)}
            disabled={busy}
            aria-label={`Perfil de ${name}`}
          >
            {TEAM_PROFILE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </td>
        <td className="adm-team__cell">
          <span className="adm-team__scope-chip team-scope__summary">{teamScopeSummary(invitation, tournaments)}</span>
        </td>
        <td className="adm-team__cell adm-team__actions">
          <button
            type="button"
            className="btn btn--ghost btn--compact adm-team__edit"
            onClick={() => openInviteScopeEditor(invitation)}
            disabled={busy}
            aria-label={`Cambiar torneos de ${name}`}
            aria-expanded={editingInviteId === invitation.id}
          >
            Cambiar torneos
          </button>
          <div className="adm-players__menu" data-team-menu>
            <button
              type="button"
              className="btn btn--ghost btn--compact adm-players__menu-trigger"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`Más acciones de ${name}`}
              onClick={() => setOpenInviteMenu(menuOpen ? null : menuKey)}
              disabled={busy}
            >
              ⋯
            </button>
            {menuOpen ? (
              <div className="adm-players__menu-list" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="adm-players__menu-item adm-players__menu-item--danger"
                  onClick={() => {
                    setOpenInviteMenu(null);
                    void handleInviteRemove(invitation);
                  }}
                >
                  Quitar del equipo
                </button>
                <small className="adm-team__menu-note">Solo le quita el acceso a tus torneos. Su cuenta no se toca.</small>
              </div>
            ) : null}
          </div>
        </td>
      </tr>
    );
  }

  const editingMember = editingUserId === null ? null : members.find((item) => item.user_id === editingUserId) ?? null;

  function renderScopeEditor(member: AdminTeamMember) {
    const busy = busyUserId === member.user_id;
    const missing = teamScopeMissing(member, tournaments);
    const missingNames = joinTournamentNames(missing.map((t) => t.name));
    const plural = missing.length > 1;
    const dirty = isTeamScopeDirty(member, editScope, tournaments, restoreMissing);
    return (
      <div className="team-scope-editor" id={SCOPE_EDITOR_ID} role="region" aria-labelledby={`${SCOPE_EDITOR_ID}-title`}>
        <h3 id={`${SCOPE_EDITOR_ID}-title`} className="team-scope-editor__title" tabIndex={-1} ref={editorTitleRef}>
          Torneos de {member.full_name}
        </h3>
        <TeamTournamentScopePicker
          idPrefix={`team-member-${member.user_id}`}
          value={editScope}
          tournaments={tournaments}
          onChange={setEditScope}
          onInteract={clearNotices}
          disabled={busy}
          personLabel={member.full_name}
        />
        {/* Avisos de estado (no responden a una acción): sin toast. */}
        {editScope.scope === "all" && missing.length > 0 ? (
          member.scope === "all" ? (
            <div className="team-scope-editor__notice" data-no-toast>
              <p>
                Hoy no entra a {missingNames}: lo quitaste desde el Equipo de {plural ? "esos torneos" : "ese torneo"}.
              </p>
              <label className="team-scope-editor__restore">
                <input
                  type="checkbox"
                  checked={restoreMissing}
                  disabled={busy}
                  onChange={(event) => {
                    clearNotices();
                    setRestoreMissing(event.target.checked);
                  }}
                />
                <span>Que vuelva a entrar también a {plural ? "esos torneos" : "ese torneo"}</span>
              </label>
            </div>
          ) : (
            <p className="team-scope-editor__notice" data-no-toast>
              Al guardar también entrará a {missingNames}, y a los torneos que crees después.
            </p>
          )
        ) : null}
        <div className="form__actions">
          <button type="button" className="btn btn--compact" onClick={() => void handleScopeSave(member)} disabled={busy || !dirty}>
            {busy ? "Guardando…" : "Guardar"}
          </button>
          <button type="button" className="btn btn--ghost btn--compact" onClick={closeScopeEditor} disabled={busy}>
            Cancelar
          </button>
          {!dirty && !busy ? <small className="muted">Sin cambios por guardar.</small> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="adm-team">
      <header className="adm-players__head">
        <div className="adm-team__intro">
          <span className="adm-players__eyebrow">Tu equipo de trabajo</span>
          <h1 className="adm-players__title">
            <span className="hint-label">
              Equipo
              <GuideHelpButton />
            </span>
            {!forbidden && !loading ? <span className="adm-players__count">{used} de {maxSubUsers}</span> : null}
          </h1>
          <p className="adm-team__lead">
            Cada quien entra con su propia cuenta y solo con lo que su perfil permite: a todos tus torneos (también los que crees después) o solo a los que marques.
            Si en un torneo necesitas un perfil distinto o dejar a alguien fuera, cámbialo desde el Equipo de ese torneo.
          </p>
        </div>
        {!forbidden && !loading ? (
          <div className="adm-players__head-actions">
            <button type="button" className="adm-players__btn adm-players__btn--primary" onClick={goToAddForm}>
              + Agregar a mi equipo
            </button>
          </div>
        ) : null}
      </header>
      {error && <p role="alert" className="form-error">{error}</p>}
      {message && <p className="form-message">{message}</p>}
      {forbidden && !loading && (
        <p className="muted">Formas parte del equipo de un organizador: él administra quién entra a sus torneos.</p>
      )}

      {!forbidden && (
        <section className="adm-team__profiles" aria-label="Perfiles">
          {TEAM_PROFILE_CARDS.map((card, index) => (
            <div key={card.value} className={`adm-team__profile${index === 0 ? " adm-team__profile--main" : ""}`}>
              <strong>{card.title}</strong>
              <span>{card.text}</span>
            </div>
          ))}
        </section>
      )}

      {!forbidden && (
        <section className="adm-players__panel adm-team__panel" aria-labelledby="adm-team-members">
          <div>
            <h2 id="adm-team-members" className="adm-team__h2">Miembros</h2>
            <p className="adm-team__sub">
              {tournamentsCount > 0
                ? `Tienes ${tournamentsCount === 1 ? "1 torneo" : `${tournamentsCount} torneos`}. Cada miembro entra a todos o solo a los que elijas.`
                : "Aún no tienes torneos: quien tenga «Todos mis torneos» entrará solo al que crees."}
            </p>
          </div>
          {loading ? (
            <p className="muted">Cargando…</p>
          ) : members.length === 0 && invitations.length === 0 ? (
            <p className="muted">Todavía no hay miembros en tu Equipo.</p>
          ) : (
            <>
              <table className="adm-team__rows team-members-table">
                <thead className="adm-team__sr">
                  <tr>
                    <th>Nombre</th>
                    <th>Perfil</th>
                    <th>Torneos</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => {
                    const editing = editingUserId === member.user_id;
                    const busy = busyUserId === member.user_id;
                    const menuOpen = openMenuId === member.user_id;
                    return (
                      <tr key={member.user_id} className={`adm-team__row${editing ? " team-members-table__row--editing adm-team__row--editing" : ""}`}>
                        <td className="adm-team__who team-members-table__name">
                          <span className="adm-team__avatar" aria-hidden="true">{initialsOf(member.full_name)}</span>
                          <span className="adm-players__who-text">
                            <strong>{member.full_name}</strong>
                            <span className="adm-players__sub adm-team__email">{member.email}</span>
                            {invitations.length > 0 ? (
                              <span className="adm-team__badges">
                                <span className="adm-team__badge adm-team__badge--sub">Sub-usuario</span>
                              </span>
                            ) : null}
                            {member.must_change_password && <small className="team-member__pending">Aún no crea su contraseña</small>}
                          </span>
                        </td>
                        <td className="adm-team__cell">
                          <select
                            className={`adm-team__profile-select adm-team__profile-select--${member.profile}`}
                            value={member.profile}
                            onChange={(event) => void handleChangeProfile(member, event.target.value)}
                            disabled={busy}
                            aria-label={`Perfil de ${member.full_name}`}
                          >
                            {TEAM_PROFILE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="adm-team__cell">
                          <span className="adm-team__scope-chip team-scope__summary">{teamScopeSummary(member, tournaments)}</span>
                        </td>
                        <td className="adm-team__cell adm-team__actions">
                          <button
                            type="button"
                            className="btn btn--ghost btn--compact adm-team__edit"
                            ref={(node) => {
                              if (node) scopeButtonRefs.current.set(member.user_id, node);
                              else scopeButtonRefs.current.delete(member.user_id);
                            }}
                            onClick={() => openScopeEditor(member)}
                            disabled={busy}
                            aria-label={`Cambiar torneos de ${member.full_name}`}
                            aria-expanded={editing}
                            aria-controls={editing ? SCOPE_EDITOR_ID : undefined}
                          >
                            Cambiar torneos
                          </button>
                          <div className="adm-players__menu" data-team-menu>
                            <button
                              type="button"
                              className="btn btn--ghost btn--compact adm-players__menu-trigger"
                              aria-haspopup="menu"
                              aria-expanded={menuOpen}
                              aria-label={`Más acciones de ${member.full_name}`}
                              onClick={() => setOpenMenuId(menuOpen ? null : member.user_id)}
                              disabled={busy}
                            >
                              ⋯
                            </button>
                            {menuOpen ? (
                              <div className="adm-players__menu-list" role="menu">
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="adm-players__menu-item adm-players__menu-item--danger"
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    void handleRemove(member);
                                  }}
                                >
                                  Quitar del Equipo
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {invitations.map(renderInvitationRow)}
                </tbody>
              </table>
              {editingMember ? renderScopeEditor(editingMember) : null}
              {editingInvite ? renderInviteScopeEditor(editingInvite) : null}
              {invitations.length > 0 ? (
                <p className="adm-team__legend muted">
                  <strong>Cuenta propia</strong> = la persona ya tenía cuenta y aceptó tu invitación. <strong>Sub-usuario</strong> = cuenta que tú creaste (cuenta en tus {maxSubUsers}).
                </p>
              ) : null}
            </>
          )}
        </section>
      )}

      {!forbidden && !loading && (
        <section className="adm-players__panel adm-team__add" aria-labelledby="adm-team-add" ref={addSectionRef}>
          <div>
            <h2 id="adm-team-add" className="adm-team__h2">Agregar a mi Equipo</h2>
            <p className="adm-team__sub">Sub-usuarios creados: {used} de {maxSubUsers}.</p>
          </div>
          <form className="form adm-team__form" onSubmit={handleAdd}>
            <div className="adm-team__segmented" role="radiogroup" aria-label="Modo de alta">
              <label className={mode === "new" ? "is-active" : undefined}>
                <input type="radio" name="admin-team-mode" checked={mode === "new"} onChange={() => handleModeChange("new")} disabled={saving} /> Crear cuenta nueva
              </label>
              <label className={mode === "existing" ? "is-active" : undefined}>
                <input type="radio" name="admin-team-mode" checked={mode === "existing"} onChange={() => handleModeChange("existing")} disabled={saving} /> Agregar una que ya creé
              </label>
              <label className={mode === "invite" ? "is-active" : undefined}>
                <input type="radio" name="admin-team-mode" checked={mode === "invite"} onChange={() => handleModeChange("invite")} disabled={saving} /> Invitar a alguien que ya usa Torneos123
                <span className="adm-team__new-tag">Nuevo</span>
              </label>
            </div>

            {mode === "invite" ? (
              <label className="adm-team__existing">
                Correo de su cuenta
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(event) => {
                    clearNotices();
                    setInviteEmail(event.target.value);
                  }}
                  disabled={saving}
                  placeholder="nombre@correo.com"
                  autoComplete="off"
                />
                <small className="muted">Escríbelo completo, tal como entra a Torneos123. No buscamos ni sugerimos cuentas.</small>
              </label>
            ) : mode === "new" ? (
              <>
                {cupoLleno && (
                  <p className="form-error" data-no-toast>
                    Ya usaste tus {maxSubUsers} sub-usuarios. Agrega uno existente o pide más cupo a Torneos123.
                  </p>
                )}
                <div className="adm-team__grid">
                  <label>
                    Nombre completo
                    <input ref={firstFieldRef} type="text" required value={newUser.full_name} onChange={(event) => setNewUser({ ...newUser, full_name: event.target.value })} disabled={saving || cupoLleno} placeholder="Nombre del colaborador" />
                  </label>
                  <label>
                    <span className="hint-label">
                      Correo
                      <FieldHint>Con este correo iniciará sesión en Torneos123.</FieldHint>
                    </span>
                    <input type="email" required value={newUser.email} onChange={(event) => setNewUser({ ...newUser, email: event.target.value })} disabled={saving || cupoLleno} placeholder="colaborador@correo.com" />
                  </label>
                  <label>
                    <span className="hint-label">
                      Contraseña inicial
                      <FieldHint>Mínimo 6 caracteres. Es de un solo uso: al entrar por primera vez tendrá que crear la suya.</FieldHint>
                    </span>
                    <input type="password" required minLength={6} value={newUser.password} onChange={(event) => setNewUser({ ...newUser, password: event.target.value })} disabled={saving || cupoLleno} autoComplete="new-password" />
                  </label>
                  <label>
                    Teléfono (opcional)
                    <input type="tel" value={newUser.phone} onChange={(event) => setNewUser({ ...newUser, phone: event.target.value })} disabled={saving || cupoLleno} placeholder="55 0000 0000" />
                  </label>
                </div>
              </>
            ) : othersSubUsers.length === 0 ? (
              <p className="muted">Todos tus sub-usuarios ya están en tu Equipo.</p>
            ) : (
              <label className="adm-team__existing">
                Sub-usuario
                <select value={existingUserId} onChange={(event) => handleExistingChange(event.target.value)} disabled={saving} required>
                  <option value="">Elige un sub-usuario…</option>
                  {othersSubUsers.map((sub) => (
                    <option key={sub.user_id} value={sub.user_id}>{sub.full_name} · {sub.email}</option>
                  ))}
                </select>
              </label>
            )}

            <div className="adm-team__field">
              <span className="hint-label adm-team__label" id="adm-team-profile-label">
                Perfil en tus torneos
                {selectedProfile && <FieldHint>{selectedProfile.hint}</FieldHint>}
              </span>
              <div className="adm-team__pills" role="radiogroup" aria-labelledby="adm-team-profile-label">
                {TEAM_PROFILE_OPTIONS.map((option) => (
                  <label key={option.value} className={`adm-team__pill${profile === option.value ? " is-active" : ""}`}>
                    <input
                      type="radio"
                      name="admin-team-profile"
                      value={option.value}
                      checked={profile === option.value}
                      onChange={() => {
                        clearNotices();
                        setProfile(option.value);
                      }}
                      disabled={saving}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Después de "quién": al elegir un sub-usuario existente se
                precargan los torneos donde ya ayuda. */}
            {mode === "existing" && othersSubUsers.length === 0 ? null : (
              <TeamTournamentScopePicker
                idPrefix="team-add"
                value={addScope}
                tournaments={tournaments}
                onChange={setAddScope}
                onInteract={clearNotices}
                disabled={saving}
              />
            )}

            {mode === "invite" ? (
              <p className="adm-team__invite-note" data-no-toast>
                <span className="adm-team__invite-note-icon" aria-hidden="true">i</span>
                <span>
                  Le llega un aviso en Torneos123 y por correo; entra a tu equipo solo cuando acepte. No cuenta en tus {maxSubUsers} sub-usuarios.
                  Su cuenta sigue siendo suya: tú no ves ni cambias su contraseña ni sus torneos.
                </span>
              </p>
            ) : null}

            <div className="form__actions">
              <button
                className="adm-players__btn adm-players__btn--primary"
                type="submit"
                disabled={saving || (mode === "new" && cupoLleno) || (mode === "existing" && othersSubUsers.length === 0)}
              >
                {mode === "invite" ? (saving ? "Enviando…" : "Enviar invitación") : saving ? "Agregando…" : "Agregar a mi Equipo"}
              </button>
              <Link className="adm-players__btn adm-players__btn--secondary" to="/admin/tournaments">Ir a mis torneos</Link>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
